import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import client from "../api/client";

const DISPLAY_TIMEOUT_MS = 30000;

const MasterPinContext = createContext(null);

const createFourDigitPin = () =>
  String(Math.floor(1000 + Math.random() * 9000));

export function MasterPinProvider({ children }) {
  const [pinStore, setPinStore] = useState({});
  const timeoutMapRef = useRef(new Map());

  useEffect(() => {
    return () => {
      timeoutMapRef.current.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      timeoutMapRef.current.clear();
    };
  }, []);

  const clearPin = (employeeId, actionKey) => {
    const normalizedEmployeeId = String(employeeId || "");
    if (!normalizedEmployeeId || !actionKey) {
      return;
    }

    setPinStore((previous) => {
      const employeePins = previous[normalizedEmployeeId];
      if (!employeePins || !employeePins[actionKey]) {
        return previous;
      }

      const nextEmployeePins = { ...employeePins };
      delete nextEmployeePins[actionKey];

      if (Object.keys(nextEmployeePins).length === 0) {
        const nextStore = { ...previous };
        delete nextStore[normalizedEmployeeId];
        return nextStore;
      }

      return {
        ...previous,
        [normalizedEmployeeId]: nextEmployeePins,
      };
    });
  };

  const generatePin = (employeeId, actionKey) => {
    const normalizedEmployeeId = String(employeeId || "");
    if (!normalizedEmployeeId || !actionKey) {
      return Promise.resolve("");
    }

    return client
      .post("/master-pin/generate", {
        employeeId: Number(normalizedEmployeeId),
        actionKey,
      })
      .then((response) => {
        const generatedPin = String(
          response?.data?.pin || createFourDigitPin()
        ).trim();
        const timeoutKey = `${normalizedEmployeeId}:${actionKey}`;
        const existingTimeout = timeoutMapRef.current.get(timeoutKey);

        if (existingTimeout) {
          window.clearTimeout(existingTimeout);
        }

        setPinStore((previous) => ({
          ...previous,
          [normalizedEmployeeId]: {
            ...(previous[normalizedEmployeeId] || {}),
            [actionKey]: generatedPin,
          },
        }));

        const timeoutId = window.setTimeout(() => {
          clearPin(normalizedEmployeeId, actionKey);
          timeoutMapRef.current.delete(timeoutKey);
        }, DISPLAY_TIMEOUT_MS);

        timeoutMapRef.current.set(timeoutKey, timeoutId);
        return generatedPin;
      });
  };

  const getPin = (employeeId, actionKey) => {
    const normalizedEmployeeId = String(employeeId || "");
    if (!normalizedEmployeeId || !actionKey) {
      return "";
    }

    return pinStore[normalizedEmployeeId]?.[actionKey] || "";
  };

  const hasPin = (employeeId, actionKey) => Boolean(getPin(employeeId, actionKey));

  const validatePin = (employeeId, actionKey, candidatePin) =>
    getPin(employeeId, actionKey) === String(candidatePin || "").trim();

  const verifyPinOverride = async (employeeId, actionKey, candidatePin) => {
    const normalizedEmployeeId = String(employeeId || "").trim();
    const normalizedPin = String(candidatePin || "").trim();

    if (!normalizedEmployeeId || !actionKey || !/^\d{4}$/.test(normalizedPin)) {
      return false;
    }

    try {
      console.log({
        employeeId: Number(normalizedEmployeeId),
        actionKey,
        pin: normalizedPin,
      });

      const response = await client.post("/master-pin/verify", {
        employeeId: Number(normalizedEmployeeId),
        actionKey,
        pin: normalizedPin,
      });

      return Boolean(response?.data?.success);
    } catch (error) {
      const status = error?.response?.status;
      if (status && status < 500) {
        return false;
      }

      console.error("Master PIN override verification failed:", error);
      return false;
    }
  };

  const value = useMemo(
    () => ({
      pinStore,
      generatePin,
      getPin,
      hasPin,
      validatePin,
      verifyPinOverride,
      clearPin,
    }),
    [pinStore]
  );

  return <MasterPinContext.Provider value={value}>{children}</MasterPinContext.Provider>;
}

export function useMasterPins() {
  const context = useContext(MasterPinContext);

  if (!context) {
    throw new Error("useMasterPins must be used within a MasterPinProvider");
  }

  return context;
}
